import { type Paise, formatPaise } from '@tailoring-crm/shared';

/**
 * Every number, date and money amount the shop reads is formatted here, through `Intl` with
 * the Indian locale — so ₹12,34,567.00 groups in lakhs and crores rather than thousands, and
 * 11/08/2026 is never mistaken for the 8th of November.
 *
 * Money is an integer count of paise everywhere in this product; the conversion to rupees
 * happens inside the shared package's `formatPaise` and nowhere else.
 */
export const FORMATTING_LOCALE = 'en-IN';

/** The shop's timezone. India has one, and it never changes for daylight saving. */
export const APP_TIME_ZONE = 'Asia/Kolkata';

/** Cached because constructing an `Intl` formatter is the expensive part, not using it. */
const formatterCache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>();

function numberFormatter(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `n:${JSON.stringify(options)}`;
  const cached = formatterCache.get(key);
  if (cached instanceof Intl.NumberFormat) return cached;
  const created = new Intl.NumberFormat(FORMATTING_LOCALE, options);
  formatterCache.set(key, created);
  return created;
}

function dateFormatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `d:${JSON.stringify(options)}`;
  const cached = formatterCache.get(key);
  if (cached instanceof Intl.DateTimeFormat) return cached;
  const created = new Intl.DateTimeFormat(FORMATTING_LOCALE, {
    timeZone: APP_TIME_ZONE,
    ...options,
  });
  formatterCache.set(key, created);
  return created;
}

/** A date as it arrives from the API (ISO string), from a picker (Date), or as epoch millis. */
export type DateInput = Date | string | number;

export function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value);
}

function isValidDate(value: Date): boolean {
  return !Number.isNaN(value.getTime());
}

/**
 * Money for display: `₹1,23,456.00`. Pass `compact` for a dense table where the paise are
 * noise (`₹1,23,456`) — never for an invoice, where every paisa must be visible.
 */
export function formatCurrency(
  paise: Paise,
  options: { compact?: boolean; withSymbol?: boolean } = {},
): string {
  const { compact = false, withSymbol = true } = options;
  return formatPaise(paise, {
    withSymbol,
    locale: FORMATTING_LOCALE,
    fractionDigits: compact ? 0 : 2,
  });
}

/** The Indian units a compact figure steps through, largest first. */
const COMPACT_UNITS = [
  { size: 1_00_00_000, suffix: 'Cr' },
  { size: 1_00_000, suffix: 'L' },
  { size: 1_000, suffix: 'K' },
] as const;

/**
 * Money as a shop owner says it out loud: `₹18.5K`, `₹1.8L`, `₹2.5Cr`.
 *
 * For a chart label or a big dashboard figure, where `₹1,82,450.00` would not fit and nobody
 * reads past the first two digits anyway. Lakh and crore rather than million and billion,
 * because "eighteen lakh" is the unit the owner thinks in and "1.8M" is a sum they would have
 * to convert. One decimal, dropped when it is `.0`. Never for anything a customer pays against —
 * the exact figure belongs beside it in a `title` or screen-reader text, so nothing is lost.
 *
 * The unit is chosen AFTER rounding, so ₹99,960 reads `₹1L` rather than the `₹100K` a unit
 * picked from the unrounded amount would give.
 */
export function formatCurrencyCompact(paise: Paise): string {
  const sign = paise < 0 ? '-' : '';
  const rupees = Math.abs(paise) / 100;

  // Walk up from the smallest unit, so the round-up spill (999.96 → 1,000) carries over.
  let figure = Math.round(rupees);
  let suffix = '';
  for (const unit of [...COMPACT_UNITS].reverse()) {
    const scaled = Math.round((rupees / unit.size) * 10) / 10;
    if (scaled >= 1) {
      figure = scaled;
      suffix = unit.suffix;
    }
  }
  return `${sign}₹${numberFormatter({ maximumFractionDigits: 1 }).format(figure)}${suffix}`;
}

/**
 * A calendar month (`YYYY-MM`) by name: `short` is `May`, `long` is `May 2026`.
 *
 * Read off the 15th at noon UTC, which is the same month in every timezone on Earth — so the
 * label can never slip into the neighbouring month whatever the formatter's zone does.
 */
export function formatMonth(month: string, style: 'short' | 'long' = 'short'): string {
  const date = new Date(`${month}-15T12:00:00Z`);
  if (!isValidDate(date)) return month;
  return dateFormatter(
    style === 'short' ? { month: 'short' } : { month: 'short', year: 'numeric' },
  ).format(date);
}

/** A plain count — orders, garments, customers — with Indian digit grouping. */
export function formatNumber(value: number, options: Intl.NumberFormatOptions = {}): string {
  return numberFormatter(options).format(value);
}

/** A ratio as a percentage: `formatPercent(0.185)` is `18.5%`. */
export function formatPercent(ratio: number, fractionDigits = 1): string {
  return numberFormatter({
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  }).format(ratio);
}

/** `11/08/2026` — the numeric form Indian shops write on a job card. */
export function formatDate(value: DateInput): string {
  const date = toDate(value);
  if (!isValidDate(date)) return '';
  return dateFormatter({ day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
}

/** `11 Aug 2026` — unambiguous, for headings and delivery dates. */
export function formatDateLong(value: DateInput): string {
  const date = toDate(value);
  if (!isValidDate(date)) return '';
  return dateFormatter({ day: '2-digit', month: 'short', year: 'numeric' }).format(date);
}

/** `11 Aug 2026, 4:30 pm` */
export function formatDateTime(value: DateInput): string {
  const date = toDate(value);
  if (!isValidDate(date)) return '';
  return dateFormatter({
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

/** `4:30 pm` */
export function formatTime(value: DateInput): string {
  const date = toDate(value);
  if (!isValidDate(date)) return '';
  return dateFormatter({ hour: 'numeric', minute: '2-digit', hour12: true }).format(date);
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * "in 3 days" / "2 hours ago", via `Intl.RelativeTimeFormat`. Used for delivery countdowns
 * and queued-write timestamps, where the exact minute matters less than the distance.
 */
export function formatRelativeTime(value: DateInput, now: DateInput = Date.now()): string {
  const date = toDate(value);
  if (!isValidDate(date)) return '';
  const diffMs = date.getTime() - toDate(now).getTime();
  const relative = new Intl.RelativeTimeFormat(FORMATTING_LOCALE, { numeric: 'auto' });

  const absMs = Math.abs(diffMs);
  if (absMs < MINUTE_MS) return relative.format(Math.round(diffMs / 1000), 'second');
  if (absMs < HOUR_MS) return relative.format(Math.round(diffMs / MINUTE_MS), 'minute');
  if (absMs < DAY_MS) return relative.format(Math.round(diffMs / HOUR_MS), 'hour');
  if (absMs < 30 * DAY_MS) return relative.format(Math.round(diffMs / DAY_MS), 'day');
  return formatDateLong(date);
}

/**
 * Whole minutes as `hh:mm` — 240 is `04:00`, 90 is `01:30`, 45 is `00:45`.
 *
 * Durations are an integer count of minutes everywhere in this product, exactly as money is an
 * integer count of paise; hours:minutes exists HERE and on the way into an input box, and nowhere
 * in between. Zero-padded because these numbers are read down a column beside each other, and a
 * ragged `4:00` above `01:30` is read as two different kinds of thing.
 *
 * Not localised through `Intl`: this is a clock-style figure a workshop reads at a glance, not a
 * sentence, and "4 hours" in a table cell is longer to scan than `04:00`.
 */
export function formatMinutes(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes));
  const hours = Math.floor(whole / 60);
  return `${String(hours).padStart(2, '0')}:${String(whole % 60).padStart(2, '0')}`;
}

/**
 * A call length, from the seconds it is stored in — `"3m 20s"`, `"45s"`, `"1h 2m"`.
 *
 * Not `formatMinutes` above, which draws a clock face (`03:20`) for a shift or a job estimate.
 * A call is a DURATION rather than a time, and `00:45` reads as a quarter to one. Seconds are
 * shown below an hour and dropped above it, because "1h 2m 13s" is a precision nobody acts on.
 */
export function formatCallDuration(seconds: number | null): string | null {
  if (seconds === null || seconds < 0) return null;
  const whole = Math.round(seconds);
  if (whole < 60) return `${whole}s`;

  const minutes = Math.floor(whole / 60);
  if (minutes < 60) {
    const rest = whole % 60;
    return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`;
  }

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/** `98765 43210` — how an Indian mobile number is read aloud and written down. */
export function formatMobile(mobile: string): string {
  const digits = mobile.replace(/\D/g, '').slice(-10);
  if (digits.length !== 10) return mobile;
  return `${digits.slice(0, 5)} ${digits.slice(5)}`;
}
